import { useCallback } from "react";
import { useSelectionContext } from "./selection-context";

export function useRegisterSelectable<T>(id: string, payload: T) {
	const { elementRefs, payloads, selectedIds, setSelectedIds } = useSelectionContext();

	// Sync payload on every render — ref mutation, no re-render triggered
	payloads.set(id, payload);

	const ref = useCallback(
		(el: HTMLElement | null) => {
			if (el) {
				elementRefs.set(id, el);
			} else {
				elementRefs.delete(id);
				payloads.delete(id);
			}
		},
		[id],
	);

	const handleSelect = useCallback(
		() => setSelectedIds(new Set([...selectedIds, id])),
		[id, selectedIds, setSelectedIds],
	);

	const handleRelease = useCallback(
		() => setSelectedIds(new Set([...selectedIds].filter((s) => s !== id))),
		[id, selectedIds, setSelectedIds],
	);

	const handleToggle = useCallback(
		() => (selectedIds.has(id) ? handleRelease() : handleSelect()),
		[id, selectedIds, handleSelect, handleRelease],
	);

	return {
		ref,
		isSelected: selectedIds.has(id),
		handleSelect,
		handleRelease,
		handleToggle,
	};
}
